/**
 * Shared language persistence helpers for the event-creator app.
 *
 * The active UI language is stored in localStorage so the user's choice
 * survives reloads. Adding a new language only requires:
 *   1. a `<lang>.json` file in `public/i18n/`
 *   2. an entry in `availableLangs` in `app.config.ts`
 *   3. (optionally) a flag code in `LanguagesComponent`
 */
export const LANGUAGE_STORAGE_KEY = 'wm-ec-lang';

/**
 * Persist the chosen language.
 */
export function storeLanguage(lang: string): void {
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
        // Ignore storage errors (e.g. SSR / private mode)
    }
}

/**
 * Resolve the language to activate on startup:
 * saved choice -> browser language -> provided fallback.
 *
 * @param availableLangs ids the app can render (e.g. ['de', 'en'])
 * @param fallback used when nothing else matches
 */
export function resolveInitialLanguage(
    availableLangs: string[],
    fallback: string
): string {
    const supports = (lang?: string | null): lang is string =>
        !!lang && availableLangs.includes(lang);

    // 1. Previously saved choice
    try {
        const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (supports(saved)) {
            return saved;
        }
    } catch {
        // Ignore storage errors
    }

    // 2. Browser language (e.g. "de-AT" -> "de")
    try {
        const browserLang = navigator?.language?.split('-')[0]?.toLowerCase();
        if (supports(browserLang)) {
            return browserLang;
        }
    } catch {
        // navigator may be unavailable during SSR
    }

    // 3. Fallback
    return fallback;
}
